import { useState, useEffect } from "react";
import { Box, FormControl, styled, Autocomplete, TextField, Checkbox } from "@mui/material";
import FormControlLabel from '@mui/material/FormControlLabel';
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Site from "../../../api/Admin/Site";
import EmailAddressTextField from "../../../components/EmailAddressField";
import WorkRequestFacility from "../../../api/WorkRequest/WorkRequestFacility";
import AuditInfoLabel from "../../../components/AuditInfoLabel";
import ModalSimpleButton from '../../../components/Modal/ModalSimpleButton';
import { GlobalButton, GlobalSecondaryButton } from "../../styles";
import WorkRequestType from "../../../api/WorkRequest/WorkRequestType";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useAuth } from "../../../context/AuthContext";
import { Roles } from "../../../constants/roles";

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  marginBottom: "25px"
}));

const workRequestFacilityValidationSchema = (invalidWorkRequestFacilityNames) => {
  return Yup.object().shape({
    facilityName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('WorkRequest Facility Name is required')
      .max(50, 'Must be 50 characters or less')
      .test("unique-name", "Work Request Facility Name already exists", (value) => {
        if (!value) return true;
        const lowerCaseValue = value.toLowerCase().trim();
        return !invalidWorkRequestFacilityNames.some((name) => name?.toLowerCase().trim() === lowerCaseValue);
      }),     
     siteName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Site Name is required'),
    workRequestType: Yup.string()
  });
}

const initialState = {
  facilityName: "",
  isActive: true,  
  primaryContact: "",
  siteName: "",
  workRequestTypes: [
      {
          "workRequestTypeName": "Container Registration"
      },
      {
          "workRequestTypeName": "Container Registration w/ Tests"
      },
      {
          "workRequestTypeName": "ExxonMobil Site-to-Site Shuttle"
      },
      {
          "workRequestTypeName": "Miscellaneous Request"
      },
      {
          "workRequestTypeName": "Shipping"
      },
      {
          "workRequestTypeName": "On-site Transport"
      }
  ]
};

const WorkRequestManagement = () => {
  const [isNewWorkRequestFacility, setIsNewWorkRequestFacility] = useState(false);
  const [workRequestFacilityData, setWorkRequestFacilityData] = useState(initialState);
  const [workRequestFacilityList, setWorkRequestFacilityList] = useState([]);
  const [workRequestTypeList, setWorkRequestTypeList] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [primaryContactError, setPrimaryContactError] = useState(false);

  const [showInactiveFacilities, setShowInactiveFacilities] = useState(false);   

  const [modalSimpleButton, setModalSimpleButton] = useState(false);
  const [modalSimpleButtonText, setModalSimpleButtonText] = useState('');
  const [modalSimpleTitle, setModalSimpleTitle] = useState('');
  const [modalSimpleText, setModalSimpleText] = useState('');

  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole([Roles.WorkRequestSME, Roles.Developer]);

  const initialValues = workRequestFacilityData;
  const invalidWorkRequestFacilityNames = workRequestFacilityList.filter(s => s.facilityName !== workRequestFacilityData.facilityName).map(s => s.facilityName);

  const filteredWorkRequestFacilities = workRequestFacilityList?.filter(f => showInactiveFacilities || f.isActive) ?? [];

  const resetData = () => setWorkRequestFacilityData(initialState);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    loadAllWorkRequestFacilities();
    loadAllSites();
    loadAllWorkRequestTypes();
  }, []);

  function loadAllWorkRequestFacilities() {
    WorkRequestFacility.getAll().then((res) => {
      if (res.length > 0) {
        let array = [];
        array.push({ facilityName: "" });
        array.push(
          ...res.map(x => ({
            ...x,
            preparationThresholdPercent: !isNaN(x.preparationThresholdPercent) && x.preparationThresholdPercent !== '' 
              ? parseFloat(x.preparationThresholdPercent).toFixed(2) : ''
          }))
        );

        setWorkRequestFacilityList(array.sort(
          (a,b) => a.facilityName.localeCompare(b.facilityName)
        ));
      }
    }); 
  }

  function loadAllWorkRequestTypes() {
    WorkRequestType.getAllActive().then((res) => {
      if (res.length > 0) {        
        setWorkRequestTypeList(res);
      }
    }); 
  }

  function loadAllSites() {
    Site.getAll().then((res) => {
      if (res.length > 0) {

        let array = [];
        array.push({ siteName: "", abbreviation: "" });
        array.push(...res);

        setSiteList(array.sort(
          (a,b) => a.siteName.localeCompare(b.siteName)
        ));
      }
    }); 
  }

  function handleWorkRequestFacilitySelection(workRequestFacility) {
    resetData();
    
    if (workRequestFacility?.facilityName) {
      setIsNewWorkRequestFacility(false);
      setWorkRequestFacilityData(workRequestFacility);
    }
  }

  function handleAddNewSite() {
    setIsNewWorkRequestFacility(true);
    resetData();
  }

  const handleSubmit = async(values) => {
    if (primaryContactError) return;
    
    setIsSaving(true);

    const updatedWorkRequestFacilityData = new WorkRequestFacility({
      facilityName: values.facilityName.trim(),
      isActive: values.isActive,      
      primaryContact: values.primaryContact,
      workRequestTypes: values.workRequestTypes,
      siteName: values.siteName
    });

    try {
      const apiAction = isNewWorkRequestFacility
        ? WorkRequestFacility.add(updatedWorkRequestFacilityData)
        : WorkRequestFacility.update(updatedWorkRequestFacilityData);

      await apiAction;

      resetData();
      openModalSimpleButton('Success', 'Work Request Facility saved successfully!', 'Ok');
      setIsNewWorkRequestFacility(false);
      loadAllWorkRequestFacilities();

    } catch (error) {
      openModalSimpleButton('Fail', `Error saving WorkRequest Facility : ${error?.message}`, 'Ok');      
    } finally {
      setIsSaving(false);
    } 
  }

  function closeModalSimpleButton() {
    setModalSimpleButton(false);
  }

  function openModalSimpleButton(title, text, buttonText) {
    setModalSimpleButtonText(buttonText)
    setModalSimpleButton(true);
    setModalSimpleTitle(title);
    setModalSimpleText(text);
  }

  function toggleShowInactiveFacilities() {
    const showInactive = !showInactiveFacilities;
    setShowInactiveFacilities(showInactive);

    if (!isNewWorkRequestFacility && !showInactive && workRequestFacilityData?.isActive === false) {
      resetData();
    }
  }

  return (
    <StyledBox flexDirection="column" justifyContent="space-between">

      <FormControlLabel sx={{ marginLeft: "5px", marginBottom: "5px"}}
        control={
          <Checkbox
            checked = {showInactiveFacilities}
            onChange={() => toggleShowInactiveFacilities()}
          />}
        label="Show inactive Work Request Facilities"         
      />

      <StyledBox>
        <FormControl sx={{ m: 1, width: "44rem", display: "flex" }} variant='outlined'>
          <StyledBox sx={{ justifyContent: "space-between" }}>
            <Autocomplete
              noOptionsText={"Loading Work Request Facilities..."}
              id="facility-select"
              value={workRequestFacilityData}
              onChange={(e, value) => { handleWorkRequestFacilitySelection(value) }}
              disablePortal
              options={filteredWorkRequestFacilities ?? []}
              getOptionLabel={(option) => option?.facilityName ?? ""}
              sx={{ width: "35rem" }}
              renderInput={(params) => <TextField {...params} variant="outlined" size="small" margin="none" label="Work Request Facility"/> }
              autoSelect
            />

            { canEdit && <GlobalSecondaryButton sx={{width:"8rem", marginLeft:"1rem" }}
                variant='contained'
                type="button"
                onClick={handleAddNewSite}
              >Add New</GlobalSecondaryButton>
            }

          </StyledBox>
        </FormControl>
      </StyledBox>

      {(workRequestFacilityData.facilityName || isNewWorkRequestFacility) && <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={() => workRequestFacilityValidationSchema(invalidWorkRequestFacilityNames)}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, handleBlur, setFieldValue, setFieldTouched, errors, touched, isValid, dirty }) => (
            <Form style={{margin:"10px", width: "40rem"}}>

              <StyledBox>

                <TextField
                  style={{width: "35rem"}}
                  name="facilityName"
                  label="Work Request Facility name"
                  variant="outlined" size="small" margin="none" 
                  value={values.facilityName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!isNewWorkRequestFacility}
                  error={touched.facilityName && Boolean(errors.facilityName)}
                  helperText={touched.facilityName && errors.facilityName}
                />
    
              </StyledBox>
    
              <StyledBox>
                <Autocomplete
                  noOptionsText={"Loading Work Request Types..."}
                  id="wr-types-select"
                  value={workRequestTypeList 
                    ? workRequestTypeList.filter(type => 
                      (values.workRequestTypes || []).some(
                        selected => selected.workRequestTypeName === type.typeName
                      )
                    ) : []}
                  onChange={(e, val) => setFieldValue('workRequestTypes', 
                                          val.map(item => ({workRequestTypeName : item.typeName})))}
                  disableCloseOnSelect
                  disablePortal
                  disabled={!canEdit || workRequestTypeList.length === 0}                  
                  options={workRequestTypeList ? workRequestTypeList: []}
                  getOptionLabel={(option) => option.typeName}
                  isOptionEqualToValue={(option, value) => option.typeName === value.typeName}
                  multiple
                  renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                  <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                  />
                                  {option.typeName}
                                </li>
                  )}
                  sx={{ width: "35rem" }}
                  renderInput={(params) => <TextField 
                    {...params} 
                    variant="outlined" 
                    size="small" 
                    margin="none" 
                    label="Work Request Types"/> }                  
                />
                <Box style={{ justifyContent: "space-between" }}>
                  <FormControlLabel
                    label="Active"
                    style={{marginLeft:"40px"}}
                    control={
                      <Checkbox
                        onChange={handleChange}
                        name="isActive"
                        checked={values.isActive}
                        disabled={!canEdit} 
                      />}
                  />                  
                </Box>
                
              </StyledBox> 

              <StyledBox>

                <EmailAddressTextField
                  key='primaryContact'
                  validatedUserEmail={values.primaryContact}
                  setValidatedUserEmail={(value) => {
                    setFieldValue('primaryContact', value);
                  }}
                  setHasErrors={setPrimaryContactError}
                  hasErrors={primaryContactError}
                  isDisabled={!canEdit}
                  isRequired={false}
                  labelText="Primary Contact Email"
                  placeholderText="Primary Contact Email"
                  size={"small"}
                  margin={"none"}
                  fontSize={16}
                  fieldWidth={"78%"}
                  showPlusMeButton={true}
                />

              </StyledBox>
    
              <StyledBox style={{marginBottom: "30px"}}>

                <Autocomplete
                  noOptionsText={"Loading Sites..."}
                  value={values.siteName}
                  onChange={(e, val) => setFieldValue('siteName', val)}
                  onBlur={() => setFieldTouched('siteName', true)}
                  disablePortal
                  disabled={!canEdit || siteList.length === 0}
                  options={siteList ? siteList.map((s) => s.siteName) : []}
                  sx={{ width: "35rem" }}
                  renderInput={(params) => <TextField 
                    {...params} 
                    name="siteName"  variant="outlined" size="small" margin="none" 
                    label="Site Name"
                    error={touched.siteName && Boolean(errors.siteName)}
                    helperText={touched.siteName && errors.siteName}
                  />}
                  autoSelect                  
                />
    
              </StyledBox>

              <AuditInfoLabel auditObject={values} />            

              { canEdit &&
                <StyledBox style={{ marginTop: "30px", marginBottom: "20px" }}>
                  <GlobalButton sx={{ width: "8rem" }}
                    variant='contained'             
                    type="submit"
                    disabled={!dirty || isSaving }
                  >
                    Save
                  </GlobalButton>
                </StyledBox>
              }
    
            </Form>
          )}
        </Formik>
      } 

      <ModalSimpleButton 
        title={modalSimpleTitle} 
        buttonText={modalSimpleButtonText} 
        buttonAction={closeModalSimpleButton} 
        open={modalSimpleButton} 
        setOpen={setModalSimpleButton}
      >
        <label>
          {modalSimpleText}
        </label>
      </ModalSimpleButton>

    </StyledBox>
  );
};

export default WorkRequestManagement;
